import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StatusBadge from '@/components/ui/StatusBadge';
import { ApiError } from '@/lib/api';
import { onboardingApi, type SiteState } from '@/lib/onboarding';
import { Field, Section } from '@/pages/partners/onboarding/components/fields';
import { btnDanger, btnGhost, btnPrimary, inputCls } from '@/pages/partners/onboarding/components/styles';

const DEFAULT_SCOPES = ['listings:read', 'stock:read', 'events:read'];
const ALL_SCOPES = ['listings:read', 'stock:read', 'events:read', 'listings:write', 'sales:write', 'sales:read'];

interface Props {
  admin: boolean;
  onToast: (message: string, detail?: string, tone?: 'success' | 'error') => void;
}

/** Настройки → «Интеграция с сайтом» (этап 1.2б, docs/SITE-API.md): адрес API сайта, каналы цен, ключи pull-API, вебхуки, карта категорий, снимки. */
export default function IntegrationTab({ admin, onToast }: Props) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<SiteState | null>(null);
  const [cfg, setCfg] = useState({ site_api_url: '', site_media_base: '', default_markup_b2b_pct: '25', low_stock_threshold: '5', listing_delivery_days_min: '2', listing_delivery_days_max: '5', site_hide_after_days: '14' });
  const [keyName, setKeyName] = useState('treejartrading.ae');
  const [keyScopes, setKeyScopes] = useState<string[]>([...DEFAULT_SCOPES]);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [map, setMap] = useState<{ hubType: string; primary: string; additional: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : 'en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

  const load = useCallback(async () => {
    try {
      const s = await onboardingApi.siteState();
      setState(s);
      setMap(s.categoryMap.map((m) => ({ hubType: m.hubType, primary: m.primary, additional: m.additional.join(', ') })));
      if (admin) {
        const v = await onboardingApi.getSettings();
        setCfg({
          site_api_url: String(v.site_api_url ?? ''), site_media_base: String(v.site_media_base ?? ''), default_markup_b2b_pct: String(v.default_markup_b2b_pct ?? 25), low_stock_threshold: String(v.low_stock_threshold ?? 5),
          listing_delivery_days_min: String(v.listing_delivery_days_min ?? 2), listing_delivery_days_max: String(v.listing_delivery_days_max ?? 5), site_hide_after_days: String(v.site_hide_after_days ?? 14),
        });
      }
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), undefined, 'error');
    }
  }, [admin, onToast, t]);
  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await onboardingApi.putSettings({
        site_api_url: cfg.site_api_url.trim() || null, site_media_base: cfg.site_media_base.trim() || null, default_markup_b2b_pct: Number(cfg.default_markup_b2b_pct), low_stock_threshold: Number(cfg.low_stock_threshold),
        listing_delivery_days_min: Number(cfg.listing_delivery_days_min), listing_delivery_days_max: Number(cfg.listing_delivery_days_max), site_hide_after_days: Number(cfg.site_hide_after_days),
      });
      onToast(t('siteint.saved'));
      await load();
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), undefined, 'error');
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    try {
      const r = await onboardingApi.testSite();
      onToast(t('siteint.testResult', { status: r.status }), r.body.slice(0, 120), r.status === 200 ? 'success' : 'error');
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), undefined, 'error');
    }
  };
  const createKey = async () => {
    try {
      const r = await onboardingApi.createKey(keyName, keyScopes);
      setNewToken(r.token);
      await load();
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), undefined, 'error');
    }
  };
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      onToast(t('siteint.copied'));
    } catch {
      /* буфер недоступен — токен виден на экране */
    }
  };
  const saveMap = async () => {
    try {
      setState(await onboardingApi.putCategoryMap(map.map((m) => ({ hubType: m.hubType.trim(), primary: m.primary.trim(), additional: m.additional.split(',').map((x) => x.trim()).filter(Boolean) }))));
      onToast(t('siteint.mapSaved'));
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), undefined, 'error');
    }
  };
  const snapshot = async () => {
    try {
      const r = await onboardingApi.snapshot();
      onToast(t('siteint.snapshotDone', { date: r.date, items: r.items }));
      await load();
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), undefined, 'error');
    }
  };
  const set = (k: keyof typeof cfg) => (e: React.ChangeEvent<HTMLInputElement>) => setCfg((c) => ({ ...c, [k]: e.target.value }));

  if (!state) return null;
  return (
    <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
      <div className="flex flex-col gap-4 xl:col-span-2">
        <Section title={t('siteint.title')}>
          <p className="mb-4 text-[12px] leading-5 text-foreground-500">{t('siteint.desc')}</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('siteint.apiUrl')} hint={t('siteint.apiUrlHint')} className="sm:col-span-2"><input className={inputCls} value={cfg.site_api_url} onChange={set('site_api_url')} disabled={!admin} placeholder="https://treejartrading.ae/api/internal/partner" /></Field>
            <Field label={t('siteint.mediaBase')} className="sm:col-span-2"><input className={inputCls} value={cfg.site_media_base} onChange={set('site_media_base')} disabled={!admin} placeholder={state.siteMediaBase} /></Field>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11.5px]">
            <StatusBadge tone={state.tokenConfigured ? 'primary' : 'danger'}>{state.tokenConfigured ? t('siteint.tokenOk') : t('siteint.tokenMissing')}</StatusBadge>
            <StatusBadge tone={state.schedulerEnabled ? 'primary' : 'neutral'} dot={false}>{t('siteint.scheduler')}: {state.schedulerEnabled ? t('siteint.on') : t('siteint.off')}</StatusBadge>
            <span className="text-foreground-500">{state.lastSnapshot ? t('siteint.lastSnapshot', { date: state.lastSnapshot.date, items: state.lastSnapshot.items }) : t('siteint.noSnapshot')}</span>
            <span className="text-foreground-500">· {t('siteint.pending', { count: state.pendingEvents })}</span>
          </div>
          <h3 className="mb-3 mt-5 text-[12.5px] font-semibold text-foreground-900">{t('siteint.channels')}</h3>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Field label={t('siteint.markupB2b')}><input className={`${inputCls} tnum`} type="number" value={cfg.default_markup_b2b_pct} onChange={set('default_markup_b2b_pct')} disabled={!admin} /></Field>
            <Field label={t('siteint.lowStock')}><input className={`${inputCls} tnum`} type="number" value={cfg.low_stock_threshold} onChange={set('low_stock_threshold')} disabled={!admin} /></Field>
            <Field label={t('siteint.deliveryDays')}>
              <div className="flex items-center gap-2">
                <input className={`${inputCls} tnum`} type="number" value={cfg.listing_delivery_days_min} onChange={set('listing_delivery_days_min')} disabled={!admin} />
                <span>–</span>
                <input className={`${inputCls} tnum`} type="number" value={cfg.listing_delivery_days_max} onChange={set('listing_delivery_days_max')} disabled={!admin} />
              </div>
            </Field>
            <Field label={t('siteint.hideAfter')}><input className={`${inputCls} tnum`} type="number" value={cfg.site_hide_after_days} onChange={set('site_hide_after_days')} disabled={!admin} /></Field>
          </div>
          {admin && (
            <div className="mt-4 flex flex-wrap gap-2 border-t border-background-200 pt-4">
              <button type="button" onClick={save} disabled={busy} className={btnPrimary}><i className="ri-save-3-line text-[13px]" /> {t('siteint.save')}</button>
              <button type="button" onClick={test} className={btnGhost}><i className="ri-pulse-line text-[13px]" /> {t('siteint.test')}</button>
              <button type="button" onClick={snapshot} className={btnGhost}><i className="ri-camera-lens-line text-[13px]" /> {t('siteint.snapshot')}</button>
            </div>
          )}
        </Section>
        <Section title={t('siteint.map')} action={admin && <button type="button" onClick={saveMap} className={btnGhost}>{t('siteint.mapSave')}</button>}>
          <p className="mb-3 text-[12px] text-foreground-500">{t('siteint.mapDesc')}</p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-[12px]">
              <thead><tr className="text-left text-[10.5px] uppercase tracking-[0.06em] text-foreground-500"><th className="py-1 pr-3">{t('siteint.mapType')}</th><th className="py-1 pr-3">{t('siteint.mapPrimary')}</th><th className="py-1">{t('siteint.mapAdditional')}</th></tr></thead>
              <tbody>
                {map.map((m, i) => (
                  <tr key={m.hubType} className="border-t border-background-200">
                    <td className="py-1 pr-3 text-foreground-900">{m.hubType}</td>
                    <td className="py-1 pr-3"><input className={`${inputCls} h-8`} value={m.primary} disabled={!admin} onChange={(e) => setMap((x) => x.map((y, j) => (j === i ? { ...y, primary: e.target.value } : y)))} /></td>
                    <td className="py-1"><input className={`${inputCls} h-8`} value={m.additional} disabled={!admin} onChange={(e) => setMap((x) => x.map((y, j) => (j === i ? { ...y, additional: e.target.value } : y)))} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
      <div className="flex flex-col gap-4">
        <Section title={t('siteint.keys')}>
          <p className="mb-3 text-[12px] text-foreground-500">{t('siteint.keysDesc')}</p>
          {admin && (
            <div className="mb-3 flex flex-col gap-2">
              <div className="flex gap-2">
                <input className={inputCls} value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder={t('siteint.keyName')} />
                <button type="button" onClick={createKey} className={btnPrimary}>{t('siteint.createKey')}</button>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11.5px] text-foreground-700">
                {ALL_SCOPES.map((sc) => (
                  <label key={sc} className="flex cursor-pointer items-center gap-1">
                    <input type="checkbox" checked={keyScopes.includes(sc)} onChange={(e) => setKeyScopes((cur) => (e.target.checked ? [...cur, sc] : cur.filter((x) => x !== sc)))} />
                    {t(`siteint.scope.${sc.replace(':', '_')}`)}
                  </label>
                ))}
              </div>
            </div>
          )}
          {newToken && (
            <div className="mb-3 rounded-md border border-primary-200 bg-primary-50 p-3 text-[11.5px] text-primary-900">
              <p className="mb-1 font-medium">{t('siteint.keyCreated')}</p>
              <code className="block break-all text-[11px]">{newToken}</code>
              <button type="button" onClick={() => void copy(newToken)} className="mt-2 cursor-pointer font-semibold text-primary-700">{t('siteint.copy')}</button>
            </div>
          )}
          {state.keys.length === 0 ? <p className="text-[12px] text-foreground-500">{t('siteint.noKeys')}</p> : (
            <ul className="divide-y divide-background-200 text-[12px]">
              {state.keys.map((k) => (
                <li key={k.id} className="flex items-center justify-between py-2">
                  <span><b className="text-foreground-900">{k.name}</b> <code className="text-foreground-500">{k.prefix}…</code><br /><span className="text-[11px] text-foreground-400">{k.lastUsedAt ? t('siteint.lastUsed', { date: fmt(k.lastUsedAt) }) : t('siteint.neverUsed')} · {k.scopes.join(', ')}</span></span>
                  {k.active ? (admin && <button type="button" onClick={() => onboardingApi.revokeKey(k.id).then(() => { onToast(t('siteint.revoked')); void load(); })} className={btnDanger}>{t('siteint.revoke')}</button>) : <StatusBadge tone="neutral" dot={false}>{t('siteint.revoked')}</StatusBadge>}
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Section title={t('siteint.webhooks')}>
          <p className="mb-3 text-[12px] text-foreground-500">{t('siteint.webhooksDesc')}</p>
          {state.webhooks.length === 0 ? <p className="text-[12px] text-foreground-500">{t('siteint.noWebhooks')}</p> : (
            <ul className="divide-y divide-background-200 text-[12px]">
              {state.webhooks.map((h) => (
                <li key={h.id} className="py-2">
                  <div className="flex items-center justify-between gap-2"><code className="break-all text-[11px]">{h.url}</code>{admin && h.active && <button type="button" onClick={() => onboardingApi.deleteWebhook(h.id).then(() => void load())} className="text-[11px] text-rose-600">{t('siteint.delete')}</button>}</div>
                  <p className="text-[11px] text-foreground-400">{h.lastStatus ?? '—'} · {fmt(h.lastDeliveryAt)} {h.lastError && <span className="text-rose-600">· {h.lastError}</span>}</p>
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Section title={t('siteint.publications')}>
          {state.publications.length === 0 ? <p className="text-[12px] text-foreground-500">{t('onb.pubNone')}</p> : (
            <ul className="divide-y divide-background-200 text-[12px]">
              {state.publications.map((p) => (
                <li key={p.id} className="flex items-center justify-between py-1.5"><span>{t('onb.pubRow', { kind: t(`onb.pubKind.${p.kind}`), status: t(`onb.pubStatus.${p.status}`), ok: p.okCount, failed: p.failedCount })}</span><span className="text-[11px] text-foreground-400">{fmt(p.finishedAt || p.createdAt)}</span></li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}
